import { CONSTANTS } from "../constants/constants";
import { IdentityProvider } from "../enums/identityProvider";
import { MessageType } from "../enums/messageType";
import { IFirmDetail } from "../models/IFirmDetail";
import { IUserDetail } from "../models/IUserDetail";
import { IUserSessionConfig } from "../models/IUserSessionConfig";
import ApplicationSettingService from "./applicationSettingService";
import LoggingService from "./loggingService";

class SessionService {
    private static instance: SessionService | null = null;

    private constructor() {
        if (SessionService.instance) {
            throw new Error("You can only create one instance!");
        }
        SessionService.instance = this;
    }

    static getInstance(): SessionService {
        if (!SessionService.instance) {
            SessionService.instance = new SessionService();
        }
        return SessionService.instance;
    }

    //Organization discovery - determines which identity provider to use
    async getFirmDetail(emailId: string): Promise<IFirmDetail> {
        try {
            LoggingService.log(
                LoggingService.isLoggingEnabled() 
                    ? `SessionService - getFirmDetail for ${emailId}` 
                    : '', 
                MessageType.Info
            );

            const apiUrl = await ApplicationSettingService.getCarpeApiEndpoint(
                localStorage.getItem(CONSTANTS.user_region)
            );

            // Using CDWeb API endpoint for firm details
            const url = `${apiUrl}api/login/FirmDiscovery/${emailId}`;

            const response = await fetch(url);

            LoggingService.log(
                LoggingService.isLoggingEnabled() 
                    ? `SessionService - Response status: ${response.status}` 
                    : '', 
                MessageType.Info
            );

            if (!response.ok) {
                const errorText = await response.text();
                LoggingService.log(
                    LoggingService.isLoggingEnabled() 
                        ? `SessionService - Response error: ${errorText}` 
                        : '', 
                    MessageType.Error
                );
                throw new Error(`Firm discovery failed: ${response.statusText}`);
            }

            const responseText = await response.text();
            
            LoggingService.log(
                LoggingService.isLoggingEnabled() 
                    ? `SessionService - Response text: ${responseText}` 
                    : '', 
                MessageType.Info
            );

            const result = JSON.parse(responseText) as IFirmDetail;

            LoggingService.log(
                LoggingService.isLoggingEnabled() 
                    ? `SessionService - Firm uses provider: ${result.UseProvider}, OrgReference: ${result.OrgReference || 'N/A'}` 
                    : '', 
                MessageType.Info
            );

            return result;
        } catch (error) {
            LoggingService.log(
                LoggingService.isLoggingEnabled() 
                    ? `SessionService - getFirmDetail error: ${error}` 
                    : '', 
                MessageType.Error
            );
            throw error;
        }
    }

    //Validate PI token and retrieve TC token from backend
    async validatePlatformIdentityUser(platformToken: string, region?: string | null): Promise<IUserDetail> {
        try {
            LoggingService.log(
                LoggingService.isLoggingEnabled() 
                    ? `SessionService - validatePlatformIdentityUser` 
                    : '', 
                MessageType.Info
            );

            const apiUrl = await ApplicationSettingService.getCarpeApiEndpoint(region || null);

            const response = await fetch(`${apiUrl}api/login/ValidateUser`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${platformToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                
                if (!errorText || errorText.includes("NotFound:Invalid user.")) {
                    LoggingService.log(
                        LoggingService.isLoggingEnabled() 
                            ? `SessionService - User not found in database` 
                            : '', 
                        MessageType.Error
                    );
                    return { userNotFound: true } as IUserDetail;
                }

                throw new Error(`Validation failed: ${response.statusText}`);
            }

            const userDetail = await response.json() as IUserDetail;

            // Handle userNotFound and schemaRefreshRunning from API response
            if (userDetail.userNotFound) {
                LoggingService.log(
                    LoggingService.isLoggingEnabled() 
                        ? `SessionService - User not found in database` 
                        : '', 
                    MessageType.Error
                );
                return userDetail;
            }

            if (userDetail.schemaRefreshRunning) {
                LoggingService.log(
                    LoggingService.isLoggingEnabled() 
                        ? `SessionService - Schema refresh is running, please try again later` 
                        : '', 
                    MessageType.Info
                );
                return userDetail;
            }

            LoggingService.log(
                LoggingService.isLoggingEnabled() 
                    ? `SessionService - User validated: ${userDetail.email}` 
                    : '', 
                MessageType.Info
            );

            return userDetail;
        } catch (error) {
            LoggingService.log(
                LoggingService.isLoggingEnabled() 
                    ? `SessionService - validatePlatformIdentityUser error: ${error}` 
                    : '', 
                MessageType.Error
            );
            throw error;
        }
    }

    //Save user session to sessionStorage
    setUserSession(
        email: string, 
        provider: IdentityProvider, 
        tcToken: string, 
        platformToken?: string
    ): void {
        try {
            LoggingService.log(
                LoggingService.isLoggingEnabled() 
                    ? `SessionService - Setting session for ${email} with provider ${provider}` 
                    : '', 
                MessageType.Info
            );

            sessionStorage.setItem("UserName", email);
            sessionStorage.setItem("IdentityProvider", provider);
            sessionStorage.setItem("X-Auth-Token", tcToken);
            sessionStorage.setItem("UserLoggedIn", "true");

            if (platformToken && provider === IdentityProvider.PI) {
                sessionStorage.setItem("PI-X-Auth-Token", platformToken);
            }

            //Extract userId from token
            try {
                const payload = JSON.parse(atob(tcToken.split('.')[1]));
                if (payload.unique_name) {
                    sessionStorage.setItem("userId", payload.unique_name);
                }
            } catch (e) {
                LoggingService.log(LoggingService.isLoggingEnabled() ? `SessionService - Could not extract userId from token` : '', MessageType.Info);
            }

            LoggingService.log(
                LoggingService.isLoggingEnabled() 
                    ? `SessionService - Session set successfully` 
                    : '', 
                MessageType.Info
            );
        } catch (error) {
            LoggingService.log(
                LoggingService.isLoggingEnabled() 
                    ? `SessionService - setUserSession error: ${error}` 
                    : '', 
                MessageType.Error
            );
            throw error;
        }
    }

    //Clear user session from sessionStorage
    clearUserSession(): void {
        try {
            LoggingService.log(LoggingService.isLoggingEnabled() ? `SessionService - Clearing session` : '', MessageType.Info);

            //Clear all session data
            sessionStorage.removeItem("UserName");
            sessionStorage.removeItem("IdentityProvider");
            sessionStorage.removeItem("X-Auth-Token");
            sessionStorage.removeItem("PI-X-Auth-Token");
            sessionStorage.removeItem("refreshToken");
            sessionStorage.removeItem("userId");
            sessionStorage.removeItem("UserLoggedIn");
            sessionStorage.removeItem("hostClientType");
            sessionStorage.removeItem("UserOrgReference");
            sessionStorage.removeItem("piToken");

            LoggingService.log(
                LoggingService.isLoggingEnabled() 
                    ? `SessionService - Session cleared` 
                    : '', 
                MessageType.Info
            );
        } catch (error) {
            LoggingService.log(
                LoggingService.isLoggingEnabled() 
                    ? `SessionService - clearUserSession error: ${error}` 
                    : '', 
                MessageType.Error
            );
        }
    }
}

const sessionService = Object.freeze(SessionService.getInstance());
export default sessionService;

